import { rebsortenListe, rebsortenText } from "../kern/fachlogik.js";
import { parseJahr, parseZahl, sortDe, uid } from "../kern/format.js";
import { FLASCHENGROESSEN, NV, SCHEMATA, TYPEN, ZUCKER_KATEGORIEN } from "../kern/konstanten.js";


/* ═══════════════════════════════════════════════════════════════
   Import / Export — Excel-Schablone
   Sechs Datenblätter (Regionen, Winzer, Weine, Jahrgänge, Kritiken,
   Getrunken) plus ein Hinweise-Blatt. Dieselbe Struktur dient als
   leere Vorlage, als Export des Bestands und als Importquelle.
   Der Kellerverlauf — getrunkene Flaschen und Kritiken auch zu
   beständelosen Jahrgängen — ist vollständig abgebildet.
   ═══════════════════════════════════════════════════════════════ */

/* SheetJS wird erst bei Bedarf vom offiziellen CDN nachgeladen, damit
   das Haupt-Bundle schlank bleibt. */
const XLSX_CDN = "https://cdn.sheetjs.com/xlsx-0.20.3/package/dist/xlsx.full.min.js";

let xlsxLadeVersprechen = null;

function ladeXLSX() {
  if (typeof window !== "undefined" && window.XLSX) return Promise.resolve(window.XLSX);
  if (xlsxLadeVersprechen) return xlsxLadeVersprechen;
  xlsxLadeVersprechen = new Promise((resolve, reject) => {
    const skript = document.createElement("script");
    skript.src = XLSX_CDN;
    skript.async = true;
    skript.onload = () => window.XLSX ? resolve(window.XLSX) : reject(new Error("Tabellen-Bibliothek nicht verfügbar."));
    skript.onerror = () => { xlsxLadeVersprechen = null; reject(new Error("Die Tabellen-Bibliothek konnte nicht geladen werden — bitte Internetverbindung prüfen.")); };
    document.head.appendChild(skript);
  });
  return xlsxLadeVersprechen;
}


const BLATT = {
  regionen: "Regionen",
  winzer: "Winzer",
  weine: "Weine",
  jahrgaenge: "Jahrgänge",
  kritiken: "Kritiken",
  getrunken: "Getrunken",
  zugaenge: "Zugänge",
  weinkarten: "Weinkarten",
  hinweise: "Hinweise",
};


/* Reihenfolge der Flaschengrößen-Spalten im Jahrgänge-Blatt — aus den
   Flaschengrößen selbst abgeleitet, damit Blatt und Datenmodell nicht
   auseinanderlaufen. Eine fehlende Spalte hieße stillschweigend
   verlorener Bestand. */
const GROESSEN_SPALTEN = FLASCHENGROESSEN.map((g) => g.kurz);

const GROESSE_ZU_KEY = Object.fromEntries(FLASCHENGROESSEN.map((g) => [g.kurz, g.key]));


const SPALTEN = {
  regionen: ["Name", "Land", "Breitengrad", "Längengrad", "Wichtige Rebsorten", "Beschreibung"],
  winzer: ["Name", "Region", "Ort", "Beschreibung"],
  weine: ["Name", "Winzer", "Typ", "Rebsorten", "Region (abweichend)", "Appellation", "Klassifikation", "Boden",
    "Label", "Beschreibung", "Lieblingswein"],
  jahrgaenge: ["Wein", "Winzer", "Jahrgang", ...GROESSEN_SPALTEN,
    "Alkohol %", "Säure g/l", "Restzucker g/l", "pH", "Zuckerkategorie", "Schwefel", "Kapsel", "Ausbau", "Verschluss", "Preis €",
    "Lagerort", "Trinkfenster von", "Trinkfenster bis"],
  kritiken: ["Wein", "Winzer", "Jahrgang", "Kritiker", "Schema", "Wert", "Anlass", "Pairing", "Notiz"],
  getrunken: ["Wein", "Winzer", "Jahrgang", "Flaschengröße", "Menge", "Datum", "Preis €"],
  zugaenge: ["Wein", "Winzer", "Jahrgang", "Flaschengröße", "Menge", "Datum", "Preis €", "Bezugsquelle"],
  weinkarten: ["Weinkarte", "Wein", "Winzer", "Jahrgang", "Flaschengröße", "Menge", "Preis €", "Notiz"],
};



const normImpex = (s) => String(s == null ? "" : s).trim().toLowerCase();


/* Typ/Schema/Größe aus lesbarem Text zurückübersetzen */
function typAusText(text) {
  const t = normImpex(text);
  if (!t) return "rot";
  for (const [key, def] of Object.entries(TYPEN)) {
    if (normImpex(key) === t || normImpex(def.label) === t) return key;
  }
  return "rot";
}

function schemaAusText(text) {
  const t = normImpex(text);
  for (const [key, def] of Object.entries(SCHEMATA)) {
    if (normImpex(key) === t || normImpex(def.label) === t || normImpex(def.kurz) === t) return key;
  }
  return "p100";
}

function groesseAusText(text) {
  const roh = String(text == null ? "" : text).trim();
  const t = normImpex(roh).replace(/\s+/g, "");
  for (const g of FLASCHENGROESSEN) {
    if (g.key === roh || normImpex(g.kurz).replace(/\s+/g, "") === t) return g.key;
  }
  const m = roh.match(/([\d.,]+)/);
  if (m) { const v = m[1].replace(",", "."); if (FLASCHENGROESSEN.some((g) => g.key === v)) return v; }
  return "0.75";
}


/* Jahrgangswert aus Zelle: „N.V." → NV-Sentinel, sonst Jahreszahl. */
function jahrgangAusText(text) {
  const t = normImpex(text);
  if (t === "n.v." || t === "nv" || t === "non vintage" || t === "ohne jahrgang") return NV;
  return parseJahr(text);
}


/* Rebsortentext „Riesling 60 % · Spätburgunder 40 %" → [{name,anteil}] */
function rebsortenAusText(text) {
  return String(text == null ? "" : text)
    .split(/[,·;]/).map((t) => t.trim()).filter(Boolean)
    .map((teil) => {
      /* Anteil sowohl als „Chardonnay 50 %" als auch in Klammern —
         „Chardonnay (50%)" — erkennen, damit er nicht im Namen landet. */
      const m = teil.match(/^(.*?)[\s]*\(?\s*(\d+(?:[.,]\d+)?)\s*%\s*\)?$/);
      if (m && m[1].trim()) return { name: m[1].trim(), anteil: parseFloat(m[2].replace(",", ".")) };
      return { name: teil, anteil: null };
    })
    .filter((r) => r.name);
}


/* ── Export: db → Zeilen je Blatt (reine Datenaufbereitung) ── */
function baueExportModell(db) {
  const regById = new Map(db.regions.map((r) => [r.id, r]));
  const winById = new Map(db.winzer.map((w) => [w.id, w]));
  const weinById = new Map(db.weine.map((w) => [w.id, w]));
  const winName = (id) => (winById.get(id) ? winById.get(id).name : "");
  const weinName = (id) => (weinById.get(id) ? weinById.get(id).name : "");
  const weinWinzerName = (id) => (weinById.get(id) ? winName(weinById.get(id).winzerId) : "");

  const regionen = [...db.regions].sort(sortDe).map((r) => ({
    "Name": r.name, "Land": r.land || "",
    "Breitengrad": r.lat != null ? r.lat : "", "Längengrad": r.lon != null ? r.lon : "",
    "Wichtige Rebsorten": r.rebsorten || "", "Beschreibung": r.beschreibung || "",
  }));

  const winzer = [...db.winzer].sort(sortDe).map((w) => ({
    "Name": w.name, "Region": regById.get(w.regionId) ? regById.get(w.regionId).name : "",
    "Ort": w.ort || "", "Beschreibung": w.beschreibung || "",
  }));

  const weine = [...db.weine].sort(sortDe).map((w) => ({
    "Name": w.name, "Winzer": winName(w.winzerId),
    "Typ": TYPEN[w.typ] ? TYPEN[w.typ].label : w.typ,
    "Rebsorten": rebsortenText(w),
    /* nur bei bewusst abweichender Region befüllen */
    "Region (abweichend)": w.regionId && regById.get(w.regionId) ? regById.get(w.regionId).name : "",
    "Appellation": w.appellation || "",
    "Klassifikation": w.klassifikation || "",
    "Boden": w.boden || "",
    "Label": w.label || "",
    "Beschreibung": w.beschreibung || "",
    "Lieblingswein": w.favorit ? "ja" : "",
  }));

  const jahrgaenge = [];
  for (const w of [...db.weine].sort(sortDe)) {
    for (const jg of [...w.jahrgaenge].sort((a, b) => b.jahr - a.jahr)) {
      const zeile = {
        "Wein": w.name, "Winzer": winName(w.winzerId),
        "Jahrgang": jg.jahr === NV ? "N.V." : jg.jahr,
      };
      for (const sp of GROESSEN_SPALTEN) {
        const n = (jg.bestaende || {})[GROESSE_ZU_KEY[sp]] || 0;
        zeile[sp] = n || "";
      }
      zeile["Alkohol %"] = jg.alkohol != null ? jg.alkohol : "";
      zeile["Säure g/l"] = jg.saeure != null ? jg.saeure : "";
      zeile["Restzucker g/l"] = jg.restzucker != null ? jg.restzucker : "";
      zeile["pH"] = jg.ph != null ? jg.ph : "";
      zeile["Zuckerkategorie"] = jg.zucker || "";
      zeile["Schwefel"] = jg.schwefel || "";
      zeile["Kapsel"] = jg.kapsel || "";
      zeile["Ausbau"] = jg.ausbau || "";
      zeile["Verschluss"] = jg.verschluss || "";
      zeile["Preis €"] = jg.preis != null ? jg.preis : "";
      zeile["Lagerort"] = jg.lagerort || "";
      zeile["Trinkfenster von"] = jg.trinkVon != null ? jg.trinkVon : "";
      zeile["Trinkfenster bis"] = jg.trinkBis != null ? jg.trinkBis : "";
      jahrgaenge.push(zeile);
    }
  }

  const kritiken = (db.kritiken || []).map((k) => ({
    "Wein": weinName(k.weinId), "Winzer": weinWinzerName(k.weinId), "Jahrgang": k.jahr,
    "Kritiker": k.kritiker || "", "Schema": SCHEMATA[k.schema] ? SCHEMATA[k.schema].label : k.schema,
    "Wert": k.wert != null ? k.wert : "", "Anlass": k.anlass || "", "Pairing": k.pairing || "", "Notiz": k.text || "",
  })).filter((z) => z.Wein);
  kritiken.forEach((z) => { if (z.Jahrgang === NV) z.Jahrgang = "N.V."; });

  const grKurz = (key) => { const g = FLASCHENGROESSEN.find((x) => x.key === key); return g ? g.kurz : key; };
  const getrunken = (db.entnahmen || []).map((e) => ({
    "Wein": weinName(e.weinId), "Winzer": weinWinzerName(e.weinId), "Jahrgang": e.jahr,
    "Flaschengröße": grKurz(e.groesse), "Menge": e.menge,
    "Datum": e.datum ? e.datum.slice(0, 10) : "", "Preis €": e.preis != null ? e.preis : "",
  })).filter((z) => z.Wein);
  getrunken.forEach((z) => { if (z.Jahrgang === NV) z.Jahrgang = "N.V."; });

  const zugaenge = (db.bewegungen || []).filter((b) => b.art === "zugang").map((b) => ({
    "Wein": weinName(b.weinId), "Winzer": weinWinzerName(b.weinId), "Jahrgang": b.jahr,
    "Flaschengröße": grKurz(b.groesse), "Menge": b.menge,
    "Datum": b.datum ? b.datum.slice(0, 10) : "", "Preis €": b.preis != null ? b.preis : "",
    "Bezugsquelle": b.haendler || "",
  })).filter((z) => z.Wein);
  zugaenge.forEach((z) => { if (z.Jahrgang === NV) z.Jahrgang = "N.V."; });

  /* Weinkarten: die Flaschen darauf sind dem Keller entnommen und
     stünden sonst nirgends in der Mappe — beim Wiedereinlesen fehlten
     sie im Bestand. Kartentitel und Position genügen; Freigaben und
     PDF-Einstellungen sind Bedienzustand und bleiben außen vor. */
  const weinkarten = [];
  for (const k of db.weinkarten || []) {
    for (const e of k.eintraege || []) {
      const w = weinById.get(e.weinId);
      if (!w) continue;
      const jg = e.jgId ? (w.jahrgaenge || []).find((j) => j.id === e.jgId) : null;
      weinkarten.push({
        "Weinkarte": k.titel || "Weinkarte",
        "Wein": w.name, "Winzer": winName(w.winzerId),
        "Jahrgang": jg ? (jg.jahr === NV ? "N.V." : jg.jahr) : "",
        "Flaschengröße": grKurz(e.groesse), "Menge": e.menge != null ? e.menge : "",
        "Preis €": e.preis != null ? e.preis : "", "Notiz": e.notiz || "",
      });
    }
  }

  return { regionen, winzer, weine, jahrgaenge, kritiken, getrunken, zugaenge, weinkarten };
}


/* Hinweistext als Zeilen (erstes Blatt der Mappe) */
function hinweiseZeilen() {
  const typen = Object.values(TYPEN).map((t) => t.label).join(", ");
  const schemata = Object.values(SCHEMATA).map((s) => `${s.label} (${s.kurz})`).join(", ");
  const groessen = FLASCHENGROESSEN.map((g) => g.kurz).join(", ");
  return [
    ["WineLake — Excel-Schablone für Import und Export"],
    [""],
    ["So funktioniert der Import:"],
    ["• Fülle die Datenblätter aus. Objekte werden über ihre Namen verknüpft."],
    ["• Ist eine Region oder ein Winzer bereits vorhanden (gleicher Name), wird damit verknüpft; sonst neu angelegt."],
    ["• Fehlt eine referenzierte Region/Winzer/Wein, wird sie mit den vorhandenen Angaben angelegt."],
    ["• Ein erneuter Import derselben Datei erzeugt keine Dubletten (Bestände werden aktualisiert, Kritiken/Getrunken abgeglichen)."],
    [""],
    ["Blatt „Regionen“:  Name (Pflicht), Land, Breitengrad/Längengrad (Dezimalgrad, für Karte & Witterung), Wichtige Rebsorten, Beschreibung."],
    ["Blatt „Winzer“:    Name (Pflicht), Region (Name), Ort, Beschreibung."],
    ["Blatt „Weine“:     Name (Pflicht), Winzer (Name), Typ, Rebsorten, Region (abweichend), Appellation, Klassifikation, Boden, Label, Beschreibung, Lieblingswein."],
    ["                   „Label“ ist die eigene Einordnung (z. B. „New Wave Chardonnay“) und bleibt im eigenen Kellerbuch."],
    ["Blatt „Jahrgänge“: Wein + Winzer + Jahrgang identifizieren den Jahrgang. Danach Bestände je Flaschengröße und die Analysewerte."],
    ["                   Für Weine ohne Jahrgang (z. B. Champagner-Cuvées) in der Spalte „Jahrgang“ N.V. eintragen."],
    ["Blatt „Kritiken“:  Wein + Winzer + Jahrgang, Kritiker, Schema, Wert (in der Skala des Schemas), Notiz."],
    ["Blatt „Getrunken“: bereits getrunkene Flaschen — Wein + Winzer + Jahrgang, Flaschengröße, Menge, Datum, Preis."],
    ["Blatt „Zugänge“:   Kaufhistorie — Wein + Winzer + Jahrgang, Flaschengröße, Menge, Kaufdatum, Kaufpreis, Bezugsquelle."],
    ["Blatt „Weinkarten“: Flaschen, die auf einer Weinkarte stehen — Kartentitel, Wein + Winzer + Jahrgang, Flaschengröße, Menge, Preis, Notiz."],
    ["                   Diese Flaschen sind dem Keller bereits entnommen; die Bestände im Blatt „Jahrgänge“ enthalten sie deshalb nicht."],
    [""],
    ["Erlaubte Werte:"],
    ["• Typ: " + typen],
    ["• Schema: " + schemata],
    ["• Flaschengröße: " + groessen],
    ["• Rebsorten-Format: „Riesling 60 % · Spätburgunder 40 %“ oder ohne Anteile „Riesling, Spätburgunder“."],
    ["• Jahrgang: Jahreszahl oder N.V. (ohne Jahrgang)."],
    ["• Zuckerkategorie: z. B. " + ZUCKER_KATEGORIEN.join(", ") + "."],
    ["• Trinkfenster: zwei Jahreszahlen (von / bis)."],
    ["• Datum: JJJJ-MM-TT."],
    [""],
    ["Hinweis: Beständelose Jahrgänge (alles getrunken) bleiben erhalten, solange sie im Blatt „Jahrgänge“, „Kritiken“ oder „Getrunken“ vorkommen."],
    ["Hinweis: Etikettenfotos und Freigabe-Links lassen sich nicht in einer Tabelle abbilden und sind daher nicht Teil der Mappe."],
  ];
}


/* ── Excel schreiben (Vorlage oder Export) ── */
async function schreibeExcel(modell, dateiname) {
  const XLSX = await ladeXLSX();
  const wb = XLSX.utils.book_new();

  const wsHinweise = XLSX.utils.aoa_to_sheet(hinweiseZeilen());
  wsHinweise["!cols"] = [{ wch: 120 }];
  XLSX.utils.book_append_sheet(wb, wsHinweise, BLATT.hinweise);

  const anhaengen = (schluessel, zeilen) => {
    const spalten = SPALTEN[schluessel];
    const ws = XLSX.utils.json_to_sheet(zeilen && zeilen.length ? zeilen : [], { header: spalten });
    /* Sicherstellen, dass die Kopfzeile immer (auch bei leeren Daten) steht */
    XLSX.utils.sheet_add_aoa(ws, [spalten], { origin: "A1" });
    ws["!cols"] = spalten.map((h) => ({ wch: Math.max(12, Math.min(40, h.length + 4)) }));
    ws["!freeze"] = { xSplit: 0, ySplit: 1 };
    XLSX.utils.book_append_sheet(wb, ws, BLATT[schluessel]);
  };
  anhaengen("regionen", modell.regionen);
  anhaengen("winzer", modell.winzer);
  anhaengen("weine", modell.weine);
  anhaengen("jahrgaenge", modell.jahrgaenge);
  anhaengen("kritiken", modell.kritiken);
  anhaengen("getrunken", modell.getrunken);
  anhaengen("zugaenge", modell.zugaenge);
  anhaengen("weinkarten", modell.weinkarten);

  const puffer = XLSX.write(wb, { type: "array", bookType: "xlsx" });
  const blob = new Blob([puffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = dateiname;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}


const leeresModell = () => ({ regionen: [], winzer: [], weine: [], jahrgaenge: [], kritiken: [], getrunken: [], zugaenge: [], weinkarten: [] });


async function exportiereBestand(db) {
  const datum = new Date().toISOString().slice(0, 10);
  await schreibeExcel(baueExportModell(db), `WineLake-Export-${datum}.xlsx`);
}

async function ladeVorlage() {
  await schreibeExcel(leeresModell(), "WineLake-Vorlage.xlsx");
}


/* ── Excel lesen → Rohzeilen je Blatt (Objekte mit Kopfzeilen-Schlüsseln) ── */
async function leseExcel(datei) {
  const XLSX = await ladeXLSX();
  const puffer = await datei.arrayBuffer();
  const wb = XLSX.read(puffer, { type: "array" });
  const blatt = (name) => {
    const ws = wb.Sheets[name];
    return ws ? XLSX.utils.sheet_to_json(ws, { defval: "" }) : [];
  };
  return {
    regionen: blatt(BLATT.regionen),
    winzer: blatt(BLATT.winzer),
    weine: blatt(BLATT.weine),
    jahrgaenge: blatt(BLATT.jahrgaenge),
    kritiken: blatt(BLATT.kritiken),
    getrunken: blatt(BLATT.getrunken),
    zugaenge: blatt(BLATT.zugaenge),
    weinkarten: blatt(BLATT.weinkarten),
  };
}


/* Feldzugriff über mehrere mögliche Kopf-Schreibweisen */
function zelle(zeile, ...namen) {
  const keys = Object.keys(zeile);
  for (const name of namen) {
    const treffer = keys.find((k) => normImpex(k) === normImpex(name));
    if (treffer != null && zeile[treffer] !== "") return zeile[treffer];
  }
  return "";
}


/* ── Import: Rohzeilen in eine neue db mergen (mit Dedup/Verknüpfung) ── */
function baueImportDb(db, roh) {
  const neu = {
    ...db,
    regions: db.regions.map((r) => ({ ...r })),
    winzer: db.winzer.map((w) => ({ ...w })),
    weine: db.weine.map((w) => ({ ...w, jahrgaenge: w.jahrgaenge.map((j) => ({ ...j, bestaende: { ...(j.bestaende || {}) } })) })),
    kritiken: [...(db.kritiken || [])],
    entnahmen: [...(db.entnahmen || [])],
    bewegungen: [...(db.bewegungen || [])],
    weinkarten: (db.weinkarten || []).map((k) => ({ ...k, eintraege: [...(k.eintraege || [])] })),
  };
  const bericht = {
    regionenNeu: 0, regionenVerknuepft: 0, winzerNeu: 0, winzerVerknuepft: 0,
    weineNeu: 0, weineAktualisiert: 0, jahrgaengeNeu: 0, jahrgaengeAktualisiert: 0,
    kritikenNeu: 0, getrunkenNeu: 0, zugaengeNeu: 0, weinkartenNeu: 0, warnungen: [],
  };

  const regByName = new Map(neu.regions.map((r) => [normImpex(r.name), r]));
  const winByName = new Map(neu.winzer.map((w) => [normImpex(w.name), w]));
  const weinKey = (winzerId, name) => winzerId + "|" + normImpex(name);
  const weinByKey = new Map(neu.weine.map((w) => [weinKey(w.winzerId, w.name), w]));

  const holeRegion = (name, extra = {}) => {
    const key = normImpex(name);
    if (!key) return null;
    let r = regByName.get(key);
    if (r) {
      /* fehlende Felder ergänzen, Vorhandenes nicht überschreiben */
      if (extra.land && !r.land) r.land = extra.land;
      if (extra.lat != null && r.lat == null) r.lat = extra.lat;
      if (extra.lon != null && r.lon == null) r.lon = extra.lon;
      if (extra.rebsorten && !r.rebsorten) r.rebsorten = extra.rebsorten;
      if (extra.beschreibung && !r.beschreibung) r.beschreibung = extra.beschreibung;
      return r;
    }
    r = {
      id: uid(), name: String(name).trim(), land: extra.land || "",
      lat: extra.lat != null ? extra.lat : null, lon: extra.lon != null ? extra.lon : null,
      rebsorten: extra.rebsorten || "", beschreibung: extra.beschreibung || "",
    };
    neu.regions.push(r); regByName.set(key, r); bericht.regionenNeu++;
    if (r.lat == null || r.lon == null) bericht.warnungen.push(`Region „${r.name}“ ohne Koordinaten angelegt — für Karte/Witterung bitte nachtragen.`);
    return r;
  };

  const holeWinzer = (name, regionName, extra = {}) => {
    const key = normImpex(name);
    if (!key) return null;
    let w = winByName.get(key);
    if (w) {
      if (extra.ort && !w.ort) w.ort = extra.ort;
      if (extra.beschreibung && !w.beschreibung) w.beschreibung = extra.beschreibung;
      if (!w.regionId && regionName) { const r = holeRegion(regionName); if (r) w.regionId = r.id; }
      return w;
    }
    const region = regionName ? holeRegion(regionName) : null;
    w = {
      id: uid(), name: String(name).trim(),
      regionId: region ? region.id : (neu.regions[0] ? neu.regions[0].id : null),
      ort: extra.ort || "", beschreibung: extra.beschreibung || "",
    };
    neu.winzer.push(w); winByName.set(key, w); bericht.winzerNeu++;
    return w;
  };

  const holeWein = (name, winzerName, extra = {}) => {
    const winzer = holeWinzer(winzerName || "Unbekannter Winzer");
    if (!winzer) return null;
    const key = weinKey(winzer.id, name);
    let w = weinByKey.get(key);
    if (w) {
      if (extra.appellation && !w.appellation) w.appellation = extra.appellation;
      if (extra.klassifikation && !w.klassifikation) w.klassifikation = extra.klassifikation;
      return w;
    }
    const rl = extra.rebsorten ? rebsortenAusText(extra.rebsorten) : [];
    const anzeige = rl.length
      ? (rl.every((r) => r.anteil != null) ? rl.map((r) => `${r.name} ${Math.round(r.anteil)} %`) : rl.map((r) => r.name)).join(" · ")
      : "";
    const regionOverride = extra.regionName ? holeRegion(extra.regionName) : null;
    w = {
      id: uid(), name: String(name).trim(), winzerId: winzer.id,
      typ: extra.typ ? typAusText(extra.typ) : "rot",
      rebsortenListe: rl, rebsorten: anzeige,
      regionId: regionOverride ? regionOverride.id : null,
      appellation: extra.appellation || "",
      klassifikation: extra.klassifikation || "",
      boden: extra.boden || "",
      label: extra.label || "",
      beschreibung: extra.beschreibung || "",
      favorit: !!extra.favorit,
      jahrgaenge: [],
    };
    neu.weine.push(w); weinByKey.set(key, w); bericht.weineNeu++;
    return w;
  };

  /* 1) Regionen */
  for (const z of roh.regionen || []) {
    const name = zelle(z, "Name");
    if (!normImpex(name)) continue;
    const vorher = regByName.has(normImpex(name));
    holeRegion(name, {
      land: String(zelle(z, "Land")).trim(),
      lat: parseZahl(zelle(z, "Breitengrad")), lon: parseZahl(zelle(z, "Längengrad")),
      rebsorten: String(zelle(z, "Wichtige Rebsorten")).trim(),
      beschreibung: String(zelle(z, "Beschreibung")).trim(),
    });
    if (vorher) bericht.regionenVerknuepft++;
  }

  /* 2) Winzer */
  for (const z of roh.winzer || []) {
    const name = zelle(z, "Name");
    if (!normImpex(name)) continue;
    const vorher = winByName.has(normImpex(name));
    holeWinzer(name, String(zelle(z, "Region")).trim(), {
      ort: String(zelle(z, "Ort")).trim(), beschreibung: String(zelle(z, "Beschreibung")).trim(),
    });
    if (vorher) bericht.winzerVerknuepft++;
  }

  /* 3) Weine */
  for (const z of roh.weine || []) {
    const name = zelle(z, "Name");
    const winzerName = zelle(z, "Winzer");
    if (!normImpex(name)) continue;
    const winzer = holeWinzer(winzerName || "Unbekannter Winzer");
    const vorher = winzer && weinByKey.has(weinKey(winzer.id, name));
    const w = holeWein(name, winzerName, {
      typ: zelle(z, "Typ"), rebsorten: zelle(z, "Rebsorten"),
      regionName: String(zelle(z, "Region (abweichend)", "Region")).trim(),
      appellation: String(zelle(z, "Appellation")).trim(),
      klassifikation: String(zelle(z, "Klassifikation")).trim(),
      boden: String(zelle(z, "Boden")).trim(),
      label: String(zelle(z, "Label")).trim(),
      beschreibung: String(zelle(z, "Beschreibung")).trim(),
      favorit: normImpex(zelle(z, "Lieblingswein")) === "ja" || normImpex(zelle(z, "Lieblingswein")) === "x",
    });
    if (w && vorher) {
      /* vorhandenen Wein aktualisieren (Typ/Rebsorten/Appellation) */
      const rl = rebsortenAusText(zelle(z, "Rebsorten"));
      if (rl.length) {
        w.rebsortenListe = rl;
        w.rebsorten = rl.every((r) => r.anteil != null)
          ? rl.map((r) => `${r.name} ${Math.round(r.anteil)} %`).join(" · ")
          : rl.map((r) => r.name).join(" · ");
      }
      if (zelle(z, "Typ")) w.typ = typAusText(zelle(z, "Typ"));
      const app = String(zelle(z, "Appellation")).trim();
      if (app) w.appellation = app;
      const kla = String(zelle(z, "Klassifikation")).trim();
      if (kla) w.klassifikation = kla;
      const bod = String(zelle(z, "Boden")).trim();
      if (bod) w.boden = bod;
      const lab = String(zelle(z, "Label")).trim();
      if (lab) w.label = lab;
      const bes = String(zelle(z, "Beschreibung")).trim();
      if (bes) w.beschreibung = bes;
      const fav = normImpex(zelle(z, "Lieblingswein"));
      if (fav === "ja" || fav === "x") w.favorit = true;
      const regN = String(zelle(z, "Region (abweichend)", "Region")).trim();
      if (regN) { const r = holeRegion(regN); if (r) w.regionId = r.id; }
      bericht.weineAktualisiert++;
    }
  }

  /* 4) Jahrgänge + Bestände */
  for (const z of roh.jahrgaenge || []) {
    const weinName = zelle(z, "Wein");
    const winzerName = zelle(z, "Winzer");
    const jahr = jahrgangAusText(zelle(z, "Jahrgang"));
    if (!normImpex(weinName) || jahr == null) continue;
    const w = holeWein(weinName, winzerName);
    if (!w) continue;
    const bestaende = {};
    for (const sp of GROESSEN_SPALTEN) {
      const n = parseZahl(zelle(z, sp));
      if (n != null && n > 0) bestaende[GROESSE_ZU_KEY[sp]] = Math.round(n);
    }
    const werte = {
      jahr,
      alkohol: parseZahl(zelle(z, "Alkohol %", "Alkohol")),
      saeure: parseZahl(zelle(z, "Säure g/l", "Säure")),
      restzucker: parseZahl(zelle(z, "Restzucker g/l", "Restzucker")),
      ph: parseZahl(zelle(z, "pH")),
      zucker: String(zelle(z, "Zuckerkategorie", "Geschmack")).trim(),
      schwefel: String(zelle(z, "Schwefel")).trim(),
      kapsel: String(zelle(z, "Kapsel")).trim(),
      ausbau: String(zelle(z, "Ausbau")).trim(),
      verschluss: String(zelle(z, "Verschluss")).trim(),
      preis: parseZahl(zelle(z, "Preis €", "Preis")),
      lagerort: String(zelle(z, "Lagerort")).trim(),
      trinkVon: parseJahr(zelle(z, "Trinkfenster von")),
      trinkBis: parseJahr(zelle(z, "Trinkfenster bis")),
      bestaende,
    };
    /* N.V. kann mehrfach vorkommen und ist über Excel nicht eindeutig
       unterscheidbar — daher wird ein bestehender N.V.-Jahrgang nur bei
       echten Jahrgängen als „vorhanden" behandelt. */
    const vorhanden = jahr !== NV ? w.jahrgaenge.find((j) => j.jahr === jahr) : w.jahrgaenge.find((j) => j.jahr === NV);
    if (vorhanden) {
      Object.assign(vorhanden, werte);
      bericht.jahrgaengeAktualisiert++;
    } else {
      const jgId = uid();
      w.jahrgaenge.push({ ...werte, id: jgId });
      bericht.jahrgaengeNeu++;
      const menge = Object.values(bestaende).reduce((s, n) => s + n, 0);
      /* Kein synthetischer Zugang, wenn das Blatt „Zugänge" echte
         Kaufeinträge für diesen Wein/Jahrgang liefert. */
      const hatEchteZugaenge = (roh.zugaenge || []).some((zz) =>
        normImpex(zelle(zz, "Wein")) === normImpex(weinName) && jahrgangAusText(zelle(zz, "Jahrgang")) === jahr);
      if (menge > 0 && !hatEchteZugaenge) {
        neu.bewegungen.push({ id: uid(), art: "zugang", weinId: w.id, jgId, jahr, groesse: "0.75", menge, datum: new Date().toISOString() });
      }
    }
  }

  /* 5) Kritiken (Dedup über Wein+Jahr+Kritiker+Wert) */
  const kritikVorhanden = (weinId, jahr, kritiker, schema, wert) =>
    neu.kritiken.some((k) => k.weinId === weinId && k.jahr === jahr
      && normImpex(k.kritiker) === normImpex(kritiker) && k.schema === schema && k.wert === wert);
  for (const z of roh.kritiken || []) {
    const weinName = zelle(z, "Wein");
    const jahr = jahrgangAusText(zelle(z, "Jahrgang"));
    if (!normImpex(weinName) || jahr == null) continue;
    const w = holeWein(weinName, zelle(z, "Winzer"));
    if (!w) continue;
    const schema = schemaAusText(zelle(z, "Schema"));
    const wert = parseZahl(zelle(z, "Wert"));
    if (wert == null) continue;
    const kritiker = String(zelle(z, "Kritiker")).trim() || "Notiz";
    if (kritikVorhanden(w.id, jahr, kritiker, schema, wert)) continue;
    const kjg = jahr !== NV ? w.jahrgaenge.find((j) => j.jahr === jahr) : w.jahrgaenge.find((j) => j.jahr === NV);
    neu.kritiken.push({ id: uid(), weinId: w.id, jgId: kjg ? kjg.id : null, jahr, kritiker, schema, wert,
      anlass: String(zelle(z, "Anlass")).trim(), pairing: String(zelle(z, "Pairing", "Speisenbegleitung")).trim(),
      text: String(zelle(z, "Notiz")).trim() });
    bericht.kritikenNeu++;
  }

  /* 6) Getrunken → entnahmen + bewegungen (Dedup über Wein+Jahr+Größe+Menge+Datum) */
  const entnahmeVorhanden = (weinId, jahr, groesse, menge, datum) =>
    neu.entnahmen.some((e) => e.weinId === weinId && e.jahr === jahr && e.groesse === groesse
      && e.menge === menge && (e.datum || "").slice(0, 10) === (datum || "").slice(0, 10));
  for (const z of roh.getrunken || []) {
    const weinName = zelle(z, "Wein");
    const jahr = jahrgangAusText(zelle(z, "Jahrgang"));
    if (!normImpex(weinName) || jahr == null) continue;
    const w = holeWein(weinName, zelle(z, "Winzer"));
    if (!w) continue;
    const groesse = groesseAusText(zelle(z, "Flaschengröße", "Flaschengroesse"));
    const menge = parseZahl(zelle(z, "Menge"));
    if (menge == null || menge <= 0) continue;
    const rohDatum = String(zelle(z, "Datum")).trim();
    const datum = rohDatum ? new Date(rohDatum).toISOString() : new Date().toISOString();
    if (entnahmeVorhanden(w.id, jahr, groesse, Math.round(menge), datum)) continue;
    let preis = parseZahl(zelle(z, "Preis €", "Preis"));
    const gjg = jahr !== NV ? w.jahrgaenge.find((j) => j.jahr === jahr) : w.jahrgaenge.find((j) => j.jahr === NV);
    if (preis == null && gjg && gjg.preis != null) preis = gjg.preis;
    const m = Math.round(menge);
    const jgId = gjg ? gjg.id : null;
    neu.entnahmen.push({ id: uid(), weinId: w.id, jgId, jahr, groesse, menge: m, preis: preis != null ? preis : null, datum });
    neu.bewegungen.push({ id: uid(), art: "entnahme", weinId: w.id, jgId, jahr, groesse, menge: m, datum });
    bericht.getrunkenNeu++;
  }

  /* 7) Zugänge (Kaufhistorie) → bewegungen (Dedup über Wein+Jahr+Größe+Menge+Datum) */
  const zugangVorhanden = (weinId, jahr, groesse, menge, datum) =>
    neu.bewegungen.some((b) => b.art === "zugang" && b.weinId === weinId && b.jahr === jahr
      && b.groesse === groesse && b.menge === menge && (b.datum || "").slice(0, 10) === (datum || "").slice(0, 10));
  for (const z of roh.zugaenge || []) {
    const weinName = zelle(z, "Wein");
    const jahr = jahrgangAusText(zelle(z, "Jahrgang"));
    if (!normImpex(weinName) || jahr == null) continue;
    const w = holeWein(weinName, zelle(z, "Winzer"));
    if (!w) continue;
    const groesse = groesseAusText(zelle(z, "Flaschengröße", "Flaschengroesse"));
    const menge = parseZahl(zelle(z, "Menge"));
    if (menge == null || menge <= 0) continue;
    const rohDatum = String(zelle(z, "Datum")).trim();
    const datum = rohDatum ? new Date(rohDatum).toISOString() : new Date().toISOString();
    const m = Math.round(menge);
    if (zugangVorhanden(w.id, jahr, groesse, m, datum)) continue;
    const zjg = jahr !== NV ? w.jahrgaenge.find((j) => j.jahr === jahr) : w.jahrgaenge.find((j) => j.jahr === NV);
    neu.bewegungen.push({
      id: uid(), art: "zugang", weinId: w.id, jgId: zjg ? zjg.id : null, jahr, groesse, menge: m, datum,
      haendler: String(zelle(z, "Bezugsquelle", "Händler", "Haendler")).trim(),
      preis: parseZahl(zelle(z, "Preis €", "Preis")),
    });
    bericht.zugaengeNeu++;
  }

  /* 8) Weinkarten. Die Bestände im Blatt „Jahrgänge" sind bereits um
     die Flaschen auf den Karten vermindert — hier wird deshalb nichts
     mehr gebucht, sondern nur die Position wiederhergestellt. */
  const karteByName = new Map((neu.weinkarten || []).map((k) => [normImpex(k.titel), k]));
  for (const z of roh.weinkarten || []) {
    const titel = String(zelle(z, "Weinkarte", "Karte", "Titel")).trim();
    const weinName = zelle(z, "Wein");
    if (!normImpex(titel) || !normImpex(weinName)) continue;
    const w = holeWein(weinName, zelle(z, "Winzer"));
    if (!w) continue;
    const jahr = jahrgangAusText(zelle(z, "Jahrgang"));
    const jg = jahr == null ? null
      : (jahr !== NV ? w.jahrgaenge.find((j) => j.jahr === jahr) : w.jahrgaenge.find((j) => j.jahr === NV));
    const groesse = groesseAusText(zelle(z, "Flaschengröße", "Flaschengroesse"));
    const menge = parseZahl(zelle(z, "Menge"));
    if (menge == null || menge <= 0) continue;
    let karte = karteByName.get(normImpex(titel));
    if (!karte) {
      karte = { id: uid(), titel, felder: {}, eintraege: [] };
      neu.weinkarten = [...(neu.weinkarten || []), karte];
      karteByName.set(normImpex(titel), karte);
    }
    const m = Math.round(menge);
    const schonDa = (karte.eintraege || []).some((e) =>
      e.weinId === w.id && (e.jgId || null) === (jg ? jg.id : null) && e.groesse === groesse && e.menge === m);
    if (schonDa) continue;
    const id = uid();
    karte.eintraege = [...(karte.eintraege || []), {
      id, weinId: w.id, jgId: jg ? jg.id : null, groesse, menge: m,
      preis: parseZahl(zelle(z, "Preis €", "Preis")),
      notiz: String(zelle(z, "Notiz")).trim(),
    }];
    neu.bewegungen.push({
      id, art: "weinkarte", weinId: w.id, jgId: jg ? jg.id : null,
      jahr: jg ? jg.jahr : jahr, groesse, menge: m, preis: null,
      datum: new Date().toISOString(), karteId: karte.id,
    });
    bericht.weinkartenNeu++;
  }

  return { neu, bericht };
}

export { BLATT, GROESSEN_SPALTEN, GROESSE_ZU_KEY, SPALTEN, XLSX_CDN, baueExportModell, baueImportDb, exportiereBestand, groesseAusText, hinweiseZeilen, jahrgangAusText, ladeVorlage, ladeXLSX, leeresModell, leseExcel, normImpex, rebsortenAusText, schemaAusText, schreibeExcel, typAusText, xlsxLadeVersprechen, zelle };
